import React, { useState, useEffect, useRef } from "react";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import Modal from "../../../components/common/Modal";
import { notifyError } from "../../../utils/notification";

const UserModal = ({
  user = {},
  onSubmit,
  onClose,
  isOpen,
  isLoading = false,
  error = "",
  mode = "edit",
}) => {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    alternativePhoneNumber: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      pinCode: "",
    },
    gstin: "",
  });
  const [formError, setFormError] = useState(""); // State for form validation errors

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
        alternativePhoneNumber: user.alternativePhoneNumber || "",
        address: {
          line1: user.address?.line1 || "",
          line2: user.address?.line2 || "",
          city: user.address?.city || "",
          state: user.address?.state || "",
          pinCode: user.address?.pinCode || "",
        },
        gstin: user.gstin || "",
      });
      setFormError(""); // Clear previous errors
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Removed 'user' from dependencies

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["line1", "line2", "city", "state", "pinCode"].includes(name)) {
      setFormData((prevData) => ({
        ...prevData,
        address: {
          ...prevData.address,
          [name]: value,
        },
      }));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateUserData = (data) => {
    const requiredFields = [
      "email",
      "name",
      "phoneNumber",
      "address.line1",
      "address.city",
      "address.state",
      "address.pinCode",
      "gstin",
    ];
    for (const field of requiredFields) {
      const keys = field.split(".");
      let value = data;
      for (const key of keys) {
        value = value[key];
        if (!value) {
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formElements = new FormData(formRef.current);
    const data = Object.fromEntries(formElements.entries());
    data.address = {
      line1: formElements.get("line1"),
      line2: formElements.get("line2"),
      city: formElements.get("city"),
      state: formElements.get("state"),
      pinCode: formElements.get("pinCode"),
    };
    if (!validateUserData(data)) {
      setFormError("Please fill in all required fields.");
      return;
    }
    setFormError(""); // Clear previous errors
    const errorMessage = await onSubmit(data);
    if (errorMessage) {
      setFormError(errorMessage); // Display specific error message
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title={mode === "edit" ? "Edit Customer" : "Add New Customer"}
      isFullScreen={true}
      showConfirmationOnClose
    >
      {formError && (
        <p className="text-sm text-danger mb-4">{formError}</p> // Display specific error
      )}
      {error && (
        <p className="text-sm text-danger mb-4">{error}</p> // Display server error
      )}
      <form ref={formRef} className="" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
          <Input
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <Input
            label="Phone Number"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            required
          />
          <Input
            label="Alternative Phone Number"
            name="alternativePhoneNumber"
            value={formData.alternativePhoneNumber}
            onChange={handleChange}
          />
          <Input
            label="Address Line 1"
            name="line1"
            value={formData.address.line1}
            onChange={handleChange}
            required
          />
          <Input
            label="Address Line 2"
            name="line2"
            value={formData.address.line2}
            onChange={handleChange}
          />
          <Input
            label="City"
            name="city"
            value={formData.address.city}
            onChange={handleChange}
            required
          />
          <Input
            label="State"
            name="state"
            value={formData.address.state}
            onChange={handleChange}
            required
          />
          <Input
            label="Pin Code"
            name="pinCode"
            value={formData.address.pinCode}
            onChange={handleChange}
            required
          />
          <Input
            label="GSTIN"
            name="gstin"
            value={formData.gstin}
            onChange={handleChange}
            required
          />
        </div>
        <div className="flex justify-end mt-6 space-x-4">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" isLoading={isLoading}>
            {mode === "edit" ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UserModal;
